import { memo, useMemo } from 'react'
import { Box, Radio, Stack, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { Exchange, exchangeToJSON } from '../../proto/generated/common'
import { selectPositionLtpsSignature } from '../../selectors/security'
import { getSecurityKey, getTwinSecurityKey } from '../../util/security'
import { buildLtpsByKey } from '../../util/positionLtpsSignature'
import type { Security } from '../../proto/generated/security'

export type QuickTradeExchangeSelectorProps = {
    availableExchanges: Exchange[]
    selectedSecurity: Security
    type: string
    onExchangeChange: (exchange: Exchange) => void
}

const QuickTradeExchangeSelector: React.FC<QuickTradeExchangeSelectorProps> = ({
    availableExchanges,
    selectedSecurity,
    type,
    onExchangeChange,
}) => {
    const exchangeKeys = useMemo(
        () =>
            availableExchanges
                .map((e) => getTwinSecurityKey(selectedSecurity, e) ?? getSecurityKey(e, selectedSecurity.code))
                .filter((key): key is string => !!key),
        [availableExchanges, selectedSecurity]
    )
    const ltpsSignature = useSelector(selectPositionLtpsSignature(exchangeKeys))
    const ltpsByKey = useMemo(
        () => buildLtpsByKey(exchangeKeys, ltpsSignature),
        [exchangeKeys, ltpsSignature]
    )
    const priceMultiplier = selectedSecurity.priceMultiplier ?? 100

    if (availableExchanges.length <= 1) {
        return null
    }

    if (type === 'modify') {
        const securityKey = getSecurityKey(selectedSecurity.exchange, selectedSecurity.code)
        const ltp = ltpsByKey.get(securityKey) ?? 0
        const price = (ltp / priceMultiplier).toFixed(2)

        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize="13px" color="text.primary" sx={{ marginRight: '4px' }}>
                    {exchangeToJSON(selectedSecurity.exchange)}
                </Typography>
                <Typography fontSize="13px" color="text.primary" className="font-feature-num">
                    ₹{price}
                </Typography>
            </Box>
        )
    }

    return (
        <Stack direction="row" spacing={1}>
            {availableExchanges.map((exchange) => {
                const securityKey = getTwinSecurityKey(selectedSecurity, exchange) ?? getSecurityKey(exchange, selectedSecurity.code)
                const ltp = ltpsByKey.get(securityKey) ?? 0
                const isSelected = exchange === selectedSecurity.exchange
                const price = (ltp / priceMultiplier).toFixed(2)

                return (
                    <Box
                        key={exchange}
                        onClick={() => onExchangeChange(exchange)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            opacity: isSelected ? 1.0 : 0.6,
                            transition: 'opacity 0.2s ease',
                            '&:hover': {
                                opacity: 1.0,
                            },
                        }}
                    >
                        <Radio
                            checked={isSelected}
                            size="small"
                            sx={{
                                color: 'text.secondary',
                                '&.Mui-checked': {
                                    color: 'primary.main',
                                },
                                transform: 'scale(0.6)',
                                padding: 0,
                                marginRight: '2px',
                            }}
                        />
                        <Typography fontSize="13px" color="text.primary" sx={{ marginRight: '4px' }}>
                            {exchangeToJSON(exchange)}
                        </Typography>
                        <Typography fontSize="13px" color="text.primary" className="font-feature-num">
                            ₹{price}
                        </Typography>
                    </Box>
                )
            })}
        </Stack>
    )
}

export default memo(QuickTradeExchangeSelector)
